import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../store/appStore';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useTheme } from '../hooks/useTheme';
import { EdgeLexMark } from '@/components/brand/EdgeLexMark';
import DesktopUpdateRailAction from '@/features/desktop/DesktopUpdateRailAction';
import { useMessageNavigationAttention } from '@/features/notifications/context/NotificationProvider';
import { hasSystemToolsAccess, realmIdFromIssuer, SYSTEM_TOOLS_PLATFORM_ROLES } from '@/security/systemToolsAccess';
import {
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  UsersIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BeakerIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  CheckIcon,
  HeartIcon,
  BuildingLibraryIcon,
  AcademicCapIcon,
  CpuChipIcon,
  EyeIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  className?: string;
}

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
  roles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const userPermissions = useUserPermissions();
  const systemToolsRealm = realmIdFromIssuer((keycloak.tokenParsed as { iss?: unknown } | undefined)?.iss);
  const { isSidebarExpanded, toggleSidebar } = useAppStore();
  const { theme, setTheme } = useTheme();
  const { unreadCount: messageUnreadCount, attentionCount: messageAttentionCount, attentionVersion: messageAttentionVersion } = useMessageNavigationAttention();



  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(['ai-workflow', 'case-management'])
  );
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  
  // Navigation data with Heroicons
  const navigationGroups: NavGroup[] = [
    {
      id: 'ai-workflow',
      label: 'AI Workflow',
      items: [
        { id: 'ai-chat', label: 'AI Chat', path: '/chat', icon: ChatBubbleLeftRightIcon },
        { id: 'agents', label: 'Agents', path: '/agents', icon: CpuChipIcon },
        { id: 'client-intake', label: 'Client Intake', path: '/intake', icon: UserPlusIcon },
      ],
    },
    {
      id: 'case-management',
      label: 'Case Management',
      items: [
        { id: 'meet', label: 'Meet', path: '/meet', icon: VideoCameraIcon },
        { id: 'tasks', label: 'Tasks', path: '/tasks', icon: ClipboardDocumentListIcon },
        { id: 'contacts', label: 'Contacts', path: '/contacts', icon: UsersIcon },
        { id: 'matters', label: 'Matters', path: '/matters', icon: BriefcaseIcon },
        { id: 'documents', label: 'Documents', path: '/documents', icon: DocumentTextIcon },
        { id: 'messages', label: 'Messages', path: '/messages', icon: ChatBubbleLeftRightIcon },
        { id: 'email', label: 'Email', path: '/email', icon: EnvelopeIcon },
        { id: 'calendar', label: 'Calendar', path: '/calendar', icon: CalendarDaysIcon, roles: ['attorney', 'admin', 'assistant'] },
      ],
    },
    {
      id: 'business-tools',
      label: 'Business Tools',
      items: [
        { id: 'billing', label: 'Billing', path: '/billing', icon: CreditCardIcon },
      ],
    },
    {
      id: 'admin-tools',
      label: 'Admin Tools',
      roles: ['admin', 'system-admin'],
      items: [
        { id: 'user-management', label: 'User Management', path: '/admin/user-management', icon: UsersIcon, roles: ['admin'] },
        { id: 'firm-settings', label: 'Firm Settings', path: '/admin/settings', icon: Cog6ToothIcon, roles: ['admin'] },
        { id: 'edgemail-admin', label: 'EdgeMail', path: '/admin/edgemail', icon: EnvelopeIcon, roles: ['admin'] },
      ],
    },
    {
      id: 'system-tools',
      label: 'System Tools',
      roles: [...SYSTEM_TOOLS_PLATFORM_ROLES],
      items: [
        { id: 'system-monitor', label: 'System Monitor', path: '/system-monitor', icon: ChartBarIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'communications', label: 'Communications', path: '/system-tools/communications', icon: ChatBubbleLeftRightIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'court-calendar-portal', label: 'Court Calendar Portal', path: '/system-tools/court-calendars', icon: CalendarDaysIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'legal-content-library', label: 'Legal Content Library', path: '/system-tools/legal-content', icon: ClipboardDocumentListIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'legal-reasoning-validation', label: 'Legal Reasoning Validation', path: '/system-tools/legal-reasoning-validation', icon: AcademicCapIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'logs', label: 'Logs', path: '/logs', icon: EyeIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'chat-test-runner', label: 'Chat Test Runner', path: '/chat_test_runner', icon: BeakerIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'sudo-dashboard', label: 'SUDO Dashboard', path: '/system-monitor/sudo', icon: ShieldCheckIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'roa-health', label: 'ROA Health', path: '/system-monitor/roa-health', icon: HeartIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'court-onboarding', label: 'Court Onboarding', path: '/system-monitor/court-onboarding', icon: BuildingLibraryIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
        { id: 'few-shot-review', label: 'Lex Training', path: '/system-monitor/few-shot-review', icon: AcademicCapIcon, roles: [...SYSTEM_TOOLS_PLATFORM_ROLES] },
      ],
    },
  ];

  // Theme options with proper icons
  const themeOptions = [
    { id: 'light', label: 'Light Mode', icon: SunIcon },
    { id: 'dark', label: 'Dark Mode', icon: MoonIcon },
    { id: 'system', label: 'System', icon: ComputerDesktopIcon }
  ];

  // Permission checking
  const hasPermission = useCallback((requiredRoles?: string[]) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (userPermissions.isLoading) return false;
    
    return requiredRoles.some(role => userPermissions.roles.includes(role));
  }, [userPermissions]);

  // Filter navigation based on permissions
  const filteredGroups = useMemo(() => {
    const filtered = navigationGroups
      .filter(group => group.id === 'system-tools'
        ? hasSystemToolsAccess(systemToolsRealm, userPermissions.roles)
        : hasPermission(group.roles))
      .map(group => ({
        ...group,
        items: group.items.filter(item => hasPermission(item.roles))
      }))
      .filter(group => group.items.length > 0);
    
    return filtered;
  }, [hasPermission, systemToolsRealm, userPermissions]);

  // Check if route is active
  const isActive = useCallback((path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Check if group has active item
  const hasActiveItem = useCallback((group: NavGroup) => {
    return group.items.some(item => isActive(item.path));
  }, [isActive]);

  // Handle navigation
  const handleNavigate = useCallback((path: string) => {
    console.log('🚀 SIDEBAR NAVIGATION:', {
      clickedPath: path,
      currentLocation: location.pathname,
      authenticated: keycloak.authenticated,
      routerReady: true
    });
    navigate(path);
    console.log('🚀 Navigate function called for:', path);
  }, [navigate, location.pathname, keycloak.authenticated]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      // Clear any local storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Logout from Keycloak
      if (keycloak.authenticated) {
        await keycloak.logout({ 
          redirectUri: window.location.origin + '/login'
        });
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Force redirect to login if logout fails
      window.location.href = '/login';
    }
  }, [keycloak]);

  // Toggle group expansion
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  // Handle theme change
  const handleThemeChange = useCallback((newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setIsThemeMenuOpen(false);
  }, [setTheme]);

  // Get user info for display
  const getUserInfo = useCallback(() => {
    if (!keycloak.tokenParsed) return null;
    
    const token = keycloak.tokenParsed;
    const name = token.name || token.preferred_username || token.given_name || token.family_name || 'User';
    const email = token.email || token.upn || token.preferred_username || 'No email available';
    const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    
    return { name, email, initials };
  }, [keycloak.tokenParsed]);

  const userInfo = getUserInfo();

  if (userPermissions.isLoading) {
    return (
      <div className="w-16 h-full bg-[var(--lex-surface-sidebar)] flex items-center justify-center border-r border-[var(--lex-border)]">
        <div className="w-5 h-5 border-2 border-[var(--lex-accent-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div 
      className={`
        bg-[var(--lex-surface-sidebar)]
        border-r border-[var(--lex-border)]
        flex flex-col h-full 
        transition-all duration-300 ease-out
        ${isSidebarExpanded ? 'w-64' : 'w-16'} 
        ${className}
      `}
    >
      
      {/* Header */}
      <div className="flex items-center p-4 border-b border-[var(--lex-border)]">
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center w-8 h-8 text-[var(--lex-text-tertiary)] hover:text-[var(--lex-text-primary)] hover:bg-[var(--lex-surface-hover)] rounded-lg transition-all duration-200 flex-shrink-0"
          title={isSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <Bars3Icon className="w-5 h-5" />
        </button>
        
        {isSidebarExpanded && (
          <div className="ml-3 flex items-center space-x-2 animate-in slide-in-from-left-2 duration-300">
            <EdgeLexMark size={28} className="flex-shrink-0" />
            <div>
              <h1 className="text-[var(--lex-text-primary)] font-bold text-lg leading-tight">EdgeLex</h1>
              <p className="text-[var(--lex-text-tertiary)] text-xs leading-tight">Legal AI Platform</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 p-3 space-y-1 ${
        isSidebarExpanded 
          ? 'overflow-y-auto scrollbar-thin scrollbar-thumb-[var(--lex-border-strong)] scrollbar-track-transparent hover:scrollbar-thumb-[var(--lex-text-tertiary)]' 
          : 'overflow-y-hidden'
      }`}>
        {filteredGroups.map((group) => {
          const isGroupExpanded = expandedGroups.has(group.id);
          const isGroupActive = hasActiveItem(group);

          return (
            <div key={group.id} className="space-y-1">
              
              {/* Group Header */}
              {isSidebarExpanded && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`
                    flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg 
                    transition-all duration-200 group
                    ${isGroupActive
                      ? 'text-[var(--lex-accent-deep)] bg-[var(--lex-accent-primary-soft)] shadow-sm'
                      : 'text-[var(--lex-text-tertiary)] hover:text-[var(--lex-text-secondary)] hover:bg-[var(--lex-surface-hover)]'
                    }
                  `}
                >
                  <span className="font-semibold">{group.label}</span>
                  <ChevronDownIcon 
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isGroupExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
              )}

              {/* Group Items */}
              <div className={`space-y-1 ${
                isSidebarExpanded ? (isGroupExpanded ? 'block' : 'hidden') : 'block'
              }`}>
                {group.items.map((item) => {
                  const itemIsActive = isActive(item.path);
                  const isMessagesItem = item.id === 'messages';
                  const hasUnreadMessages = isMessagesItem && messageUnreadCount > 0;
                  const IconComponent = item.icon;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigate(item.path)}
                      className={`
                        flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200 group
                        ${isSidebarExpanded ? 'justify-start space-x-3' : 'justify-center'}
                        ${itemIsActive
                          ? 'bg-[var(--lex-accent-primary)] text-[var(--lex-accent-contrast)] shadow-lg'
                          : hasUnreadMessages
                            ? 'bg-[var(--lex-accent-primary-soft)] text-[var(--lex-text-primary)] shadow-sm'
                          : 'text-[var(--lex-text-tertiary)] hover:text-[var(--lex-text-primary)] hover:bg-[var(--lex-surface-hover)]'
                        }
                      `}
                      title={!isSidebarExpanded
                        ? hasUnreadMessages ? `${item.label} (${messageUnreadCount} unread)` : item.label
                        : undefined}
                      aria-label={hasUnreadMessages ? `${item.label}, ${messageUnreadCount} unread` : item.label}
                    >
                      <span
                        key={isMessagesItem ? messageAttentionVersion : item.id}
                        className={`relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${
                          isMessagesItem && messageAttentionVersion > 0 ? 'lex-message-nav-pulse' : ''
                        }`}
                      >
                        <IconComponent className="w-5 h-5" />
                        {/* R2 (2026-09-08), Slack's rule: a number only for what names you
                            (mentions + direct messages); a quiet dot for everything else unread. */}
                        {isMessagesItem && messageAttentionCount > 0 ? (
                          <span
                            aria-hidden="true"
                            data-testid="messages-nav-attention"
                            className="absolute -right-2 -top-2 inline-flex min-w-5 items-center justify-center rounded-full bg-[var(--lex-rail-unread-badge)] px-1 text-[10px] font-bold leading-5 text-white shadow-sm ring-2 ring-[var(--lex-surface-sidebar)]"
                          >
                            {messageAttentionCount > 99 ? '99+' : messageAttentionCount}
                          </span>
                        ) : hasUnreadMessages ? (
                          <span
                            aria-hidden="true"
                            data-testid="messages-nav-unread-dot"
                            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[var(--lex-rail-unread-badge)] ring-2 ring-[var(--lex-surface-sidebar)]"
                          />
                        ) : null}
                      </span>
                      {isSidebarExpanded && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {messageUnreadCount > 0 ? `${messageUnreadCount} unread Messages` : 'No unread Messages'}
      </span>

      {/* User Menu & Theme Switcher */}
      <div className="border-t border-[var(--lex-border)] p-3 space-y-2">

        <DesktopUpdateRailAction expanded={isSidebarExpanded} />
        
        {/* Theme Switcher */}
        <div className="relative">
          <button
            onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
            className={`
              flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200
              ${isSidebarExpanded ? 'justify-between' : 'justify-center'}
              text-[var(--lex-text-tertiary)] hover:text-[var(--lex-text-primary)] hover:bg-[var(--lex-surface-hover)]
            `}
            title={!isSidebarExpanded ? 'Change theme' : undefined}
          >
            <div className={`flex items-center ${isSidebarExpanded ? 'space-x-3' : ''}`}>
              {theme === 'light' && <SunIcon className="w-5 h-5" />}
              {theme === 'dark' && <MoonIcon className="w-5 h-5" />}
              {theme === 'system' && <ComputerDesktopIcon className="w-5 h-5" />}
              {isSidebarExpanded && (
                <span className="text-sm font-medium">Theme</span>
              )}
            </div>
            {isSidebarExpanded && (
              <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${
                isThemeMenuOpen ? 'rotate-180' : ''
              }`} />
            )}
          </button>

          {/* Theme Dropdown */}
          {isThemeMenuOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsThemeMenuOpen(false)}
              />
              <div className={`
                absolute bottom-full mb-1 
                bg-[var(--lex-surface-card)]
                border border-[var(--lex-border)]
                rounded-lg shadow-xl backdrop-blur-sm z-50
                animate-in fade-in-0 zoom-in-95 duration-200
                ${isSidebarExpanded ? 'left-0 right-0' : 'left-0 w-48'}
              `}>
                {themeOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = theme === option.id;
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleThemeChange(option.id as 'light' | 'dark' | 'system')}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm text-[var(--lex-text-secondary)] hover:bg-[var(--lex-surface-hover)] first:rounded-t-lg last:rounded-b-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent className="w-4 h-4" />
                        <span>{option.label}</span>
                      </div>
                      {isSelected && (
                        <CheckIcon className="w-4 h-4 text-[var(--lex-accent-primary)]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* User Menu */}
        {userInfo && (
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className={`
                flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 group
                ${isSidebarExpanded ? 'space-x-3' : 'justify-center'}
                text-[var(--lex-text-tertiary)] hover:text-[var(--lex-text-primary)] hover:bg-[var(--lex-surface-hover)]
              `}
              title={!isSidebarExpanded ? `${userInfo.name} (${userInfo.email})` : undefined}
            >
              <div className="w-8 h-8 bg-[var(--lex-accent-primary)] rounded-full flex items-center justify-center text-[var(--lex-accent-contrast)] text-sm font-semibold flex-shrink-0">
                {userInfo.initials}
              </div>
              {isSidebarExpanded && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-[var(--lex-text-primary)] truncate">{userInfo.name}</p>
                    <p className="text-xs text-[var(--lex-text-tertiary)] truncate">{userInfo.email}</p>
                  </div>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`} />
                </>
              )}
            </button>

            {/* User Dropdown */}
            {isUserMenuOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setIsUserMenuOpen(false)}
                />
                <div className={`
                  absolute bottom-full mb-1 
                  bg-[var(--lex-surface-card)]
                  border border-[var(--lex-border)]
                  rounded-lg shadow-xl backdrop-blur-sm z-50
                  animate-in fade-in-0 zoom-in-95 duration-200
                  ${isSidebarExpanded ? 'left-0 right-0' : 'left-0 w-48'}
                `}>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-[var(--lex-status-danger)] hover:bg-[color-mix(in oklab, var(--lex-status-danger) 18%, transparent)] rounded-lg transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar; 
